<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="800"
      persistent
      scrollable
    >
      <v-card
        class="d-flex flex-column"
        max-height="80vh"
      >
        <v-app-bar
          color="cyan"
          dark
          flat
        >
          <v-toolbar-title class="text-h5">
            Issue ECO Member Card
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="_ => onCancelIssueEcoMemberPass()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-app-bar>

        <v-card-text>
          <v-sheet
            rounded
            class="pt-3"
          >
            <v-card rounded="lg">
              <v-card-title class="d-flex justify-space-between align-center">
                <div class="text-h5 text-medium-emphasis ps-2">
                  {{ partyFullName }}
                </div>
              </v-card-title>

              <v-divider class="mb-2" />

              <v-card-text>
                <div
                  v-if="previouslyIssued"
                  class="red--text"
                >
                  <strong>
                    This party was last issued an ECO Member Card on {{ issuedAt }} by {{ issuedBy }}
                  </strong>
                </div>
                <div class="text-overline mb-1">
                  Member Since:
                </div>
                <div class="text-medium-emphasis mb-1">
                  {{ partyJoinedSince }}
                </div>
                <div class="text-overline mb-1">
                  Email Contact:
                </div>
                <div
                  v-if="partyEmails.any"
                  class="text-medium-emphasis mb-1"
                >
                  <v-checkbox
                    v-for="(partyEmail, idx) of selectableEmails"
                    :key="idx"
                    v-model="selectedPartyEmails"
                    :label="partyEmail"
                    :value="partyEmail"
                  />
                </div>
                <div
                  v-else
                >
                  <v-alert
                    outlined
                    type="warning"
                    prominent
                    border="left"
                  >
                    No valid recipient emails.
                  </v-alert>
                </div>
                <div class="text-overline mb-1">
                  Phone Contact:
                </div>
                <div
                  v-if="partyPhones.any"
                  class="text-medium-emphasis mb-1"
                >
                  <v-checkbox
                    v-for="(partyPhone, idx) of selectablePhones"
                    :key="idx"
                    v-model="selectedPartyPhones"
                    :label="`${partyPhone.phone_number} ${partyPhone.note}`"
                    :value="partyPhone"
                  />
                </div>
                <div
                  v-else
                >
                  <v-alert
                    outlined
                    type="warning"
                    prominent
                    border="left"
                  >
                    No valid recipient phones.
                  </v-alert>
                </div>
                <div
                  class="red--text text-overline mb-1"
                >
                  MESSAGE LANGUAGE:
                  <v-radio-group
                    v-model="selectedMessageLanguageCode"
                  >
                    <v-radio
                      :label="messageLanguages.en_us.name"
                      :value="messageLanguages.en_us.code"
                    />
                    <v-radio
                      :label="messageLanguages.es_es.name"
                      :value="messageLanguages.es_es.code"
                    />
                  </v-radio-group>
                </div>
              </v-card-text>

              <v-divider class="mt-2" />

              <v-card-actions />
            </v-card>
          </v-sheet>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary"
            text
            @click="_ => onCancelIssueEcoMemberPass()"
          >
            cancel
          </v-btn>
          <v-btn
            :disabled="!canIssueEcoMemberPass"
            color="primary"
            @click="confirmationDialog = !confirmationDialog"
          >
            {{ previouslyIssued ? 'Re-issue' : 'Issue' }} ECO Member Pass
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmationDialog
      cancel-button-color="secondary darken-1"
      confirm-button-color="primary darken-1"
      title="Issue ECO Member Pass"
      :visible="confirmationDialog"
      @canceled="_ => onCancelIssueEcoMemberPass()"
      @confirmed="onConfirmIssueEcoMemberPass"
    />
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

import { alertHandler } from '@/alertHandler'

import ConfirmationDialog from '@/components/Dialogs/Common/ConfirmationDialog.vue'

import { INTEGRATION_NAMESPACE } from '@/store/integrations'
import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { partyName } from '@/utilities'

export default {
  name: 'IssueEcoMemberCardDialog',

  components: {
    ConfirmationDialog,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      confirmationDialog: false,
      messageLanguages: {
        en_us: {
          code: 'en',
          name: 'English',
        },

        es_es: {
          code: 'es-ES',
          name: 'Spanish',
        },
      },

      selectedMessageLanguageCode: null,
      selectedPartyEmails: [],
      selectedPartyPhones: [],
    }
  },

  computed: {
    canIssueEcoMemberPass () {
      return this.selectedPartyEmails.length > 0 || this.selectedPartyPhones.length > 0
    },

    dialog: {
      get () { return this.value },

      set (val) {
        this.$emit('input', val)
      },
    },

    issuedAt () {
      const createdAt = this.latestEcoMemberPass?.created_at
      if (!createdAt) return

      return moment(createdAt).format('MM/DD/YYYY hh:mm:ss')
    },

    issuedBy () {
      return this.latestEcoMemberPass?.user.full_name
    },

    latestEcoMemberPass () {
      return this.peParty.eco_member_passes[0]
    },

    partyEmails () {
      const result = { any: false, emails: [] }
      this.peParty?.emails?.forEach(email => {
        if (email.address) result.emails.push(email.address)
      })

      result.any = result.emails.length > 0
      return result
    },

    partyFullName () {
      return partyName(this.peParty)
    },

    partyJoinedSince () {
      return moment(this.peParty.created_at).format('MM/DD/YYYY')
    },

    partyPhones () {
      const textableTypes = ['mt', 'wa']
      const textablePhones = []

      this.peParty?.phones?.forEach(phone => {
        if (phone.country_prefix && phone.phone_number && textableTypes.includes(phone.phone_type)) {
          if (phone.phone_type === 'mt' && phone.preferred_text === true) {
            phone.note = ''
            textablePhones.push(phone)
          } else if (phone.phone_type === 'wa' && this.twilioHasWhatsAppNumber) {
            phone.note = ' (WHATSAPP)'
            textablePhones.push(phone)
          }
        }
      })

      const result = { any: false, phone_numbers: [] }
      textablePhones.forEach(phone => {
        let countryPrefix = ''
        if (!phone.country_prefix.includes('+')) countryPrefix = '+'
        const phoneNumber = `${countryPrefix}${phone.country_prefix}${phone.phone_number}`
        result.phone_numbers.push({
          id: phone.id,
          note: phone.note,
          phone_number: phoneNumber,
          type: phone.phone_type,
        })
      })

      result.any = result.phone_numbers.length > 0
      return result
    },

    previouslyIssued () {
      return this.peParty?.eco_member_passes?.length > 0
    },

    selectableEmails () {
      return this.partyEmails.emails
    },

    selectablePhones () {
      return this.partyPhones.phone_numbers
    },

    twilioHasWhatsAppNumber () {
      return this.features?.twilio?.enabled && this.features?.twilio?.has_whatsapp
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
    ]),
  },

  created () {
    this.selectedMessageLanguageCode = this.primaryLanguage()
  },

  methods: {
    onCancelIssueEcoMemberPass () {
      this.confirmationDialog = false
      this.dialog = false

      this.selectedPartyEmails = []
      this.selectedPartyPhones = []
    },

    onConfirmIssueEcoMemberPass () {
      this.issueECOMemberPass({
        emails: this.selectedPartyEmails,
        message_language_code: this.selectedMessageLanguageCode,
        party_id: this.peParty.id,
        phone_number_ids: this.selectedPartyPhones.map(e => e.id),
      }).
        then(_ => {
          this.displayAlert('success', 'Eco Member Pass issued.')
        }).
        catch(({ data }) => {
          this.displayAlert('error', data.message || 'Internal Server Error')
        }).
        finally(_ => {
          this.onCancelIssueEcoMemberPass()
        })
    },

    primaryLanguage () {
      let language = null
      switch (this.peParty.primary_language) {
        case this.messageLanguages.es_es.name:
          language = this.messageLanguages.es_es.code
          break
        default:
          language = this.messageLanguages.en_us.code
          break
      }

      return language
    },

    ...mapActions(INTEGRATION_NAMESPACE, [
      'issueECOMemberPass',
    ]),
  },
}
</script>
